/**
 * Edit Scenarios chart result renderer.
 *
 * Uses the original (unmodified) ScenarioManager GenerateChartPanel —
 * not the AgenticV2-adapted fork used by ChartVizResult — with
 * showScenarios={false} so PreviewData (recalculated) renders as
 * dotted lines alongside PublishedData (original) as solid lines.
 *
 * Accepts the result card's chart_payload shape directly, rather than
 * a chat-message wrapper.
 */
import React, { useMemo } from 'react';
import { find, findIndex, head, includes, last, map, slice } from 'lodash';
import { enqueueSnackbar } from 'notistack';

import { IChartDataSeries } from 'src/components/ScenarioManager/EditAssumptionsDetail/OutputsPanel';
import { dateFormats } from 'src/components/ScenarioManager/EditAssumptionsDetail/OutputsPanel/FormatChartPanel';
import {
  formatParameters,
  IFormatOutputParameters,
} from 'src/components/Visualization/formatParameters';
import { useData } from 'src/hooks/useData';
import useSetState from 'src/hooks/useSetState';
import { RowIdentifier, Timescale } from 'src/types/models';

import GenerateChartPanel, {
  chartDataTypes,
  chartTypes,
  OutputsTableRef,
} from './charts/ESGenerateChartPanel';

interface EditScenariosChartResultProps {
  chartPayload: Record<string, unknown>;
  // Optional handle onto the Original/Recalculated table's export API —
  // forwarded to ESGenerateChartPanel (see its prop comment). Used by
  // the exec What-If RESULT screen's "Download (Excel)".
  tableExportRef?: React.Ref<OutputsTableRef>;
  // Forwarded to ESGenerateChartPanel: drops the panel Container's 24px
  // gutters so chart + table sit flush with the host's left edge (exec
  // What-If RESULT alignment). Analyst cards omit it and keep gutters.
  disableGutters?: boolean;
  // Exec chart frame (2026-09-07): forwarded to ESGenerateChartPanel — see its prop comment.
  frameChart?: boolean;
}

const transformChartData = (data: any) => {
  if (!data) return [];
  return (data || []).map((item: any) => ({
    ...item,
    visible: true,
  }));
};

export const EditScenariosChartResult: React.FC<
  EditScenariosChartResultProps
> = ({ chartPayload, tableExportRef, disableGutters, frameChart }) => {
  const chartData = chartPayload.data as Record<string, unknown> | undefined;
  const requestContext = chartPayload.request_context as
    Record<string, unknown> | undefined;

  const modelInstanceId = requestContext?.model_instance_id as
    number | undefined;
  const rowIdentifierIds = requestContext?.row_identifier_ids as
    number[] | undefined;
  const timescaleString = (
    requestContext?.timescales as string[] | undefined
  )?.[0];

  const chartState = useMemo(
    () => ({
      selectedChartTab: 'chart1',
      chartTabs: [
        {
          eventKey: 'chart1',
          title: (chartData as any)?.ChartName ?? 'Chart 1',
        },
      ],
      chartData: {
        chart1: {
          PreviewData: transformChartData(
            (chartData as any)?.PreviewData ?? []
          ),
          PublishedData: transformChartData(
            (chartData as any)?.PublishedData ?? []
          ),
          Dates: (chartData as any)?.Dates || [],
        },
      },
      refreshedChartData: {
        chart1: {
          PreviewData: transformChartData(
            (chartData as any)?.PreviewData ?? []
          ),
          PublishedData: transformChartData(
            (chartData as any)?.PublishedData ?? []
          ),
          Dates: (chartData as any)?.Dates || [],
        },
      },
      selectedChartRange: { chart1: null },
      selectedChartRangeIndex: { chart1: undefined },
      selectedChartType: { chart1: chartTypes[0].id },
      selectedChartDataType: { chart1: chartDataTypes[0].id },
      isPanelCollapsed: { chart1: false },
    }),
    [chartData]
  );

  const [state, setState] = useSetState<any>(chartState);

  const { data } = useData<{ rowIdentifiers?: RowIdentifier[] }>(
    () => ({
      rowIdentifiers: modelInstanceId
        ? `/instances/${modelInstanceId}/row_identifiers`
        : undefined,
    }),
    [modelInstanceId]
  );

  const selectedRowIdentifier = useMemo(() => {
    if (data?.rowIdentifiers && rowIdentifierIds) {
      return data.rowIdentifiers.find(
        (r) => rowIdentifierIds.includes(r.id) && r.Type === 'Output'
      );
    }
  }, [data?.rowIdentifiers, rowIdentifierIds]);

  const selectedChartTab = find(state.chartTabs, {
    eventKey: state.selectedChartTab,
  });

  const handleChangeChartRange = (
    startRange: string,
    endRange: string
  ): void => {
    if (
      startRange === head(state.selectedChartRange[state.selectedChartTab]) &&
      endRange === last(state.selectedChartRange[state.selectedChartTab])
    ) {
      return;
    }
    const startIndex = findIndex(
      state.refreshedChartData[state.selectedChartTab].Dates,
      (item: any) => item.Date === startRange
    );
    const endIndex = findIndex(
      state.refreshedChartData[state.selectedChartTab].Dates,
      (item: any) => item.Date === endRange
    );
    if (startIndex > endIndex) {
      enqueueSnackbar('Start date should be less than end date', {
        variant: 'error',
      });
    } else {
      const newChartDataSeries = {
        PublishedData: map(
          state.refreshedChartData[state.selectedChartTab].PublishedData,
          (item) => ({
            id: item.id,
            scenario: item.scenario,
            segment: item.segment,
            name: item.name,
            values: slice(
              (
                find(
                  state.refreshedChartData[state.selectedChartTab]
                    .PublishedData,
                  { id: item.id }
                ) as IChartDataSeries
              ).values,
              startIndex,
              endIndex + 1
            ),
            visible: item.visible,
          })
        ),
        PreviewData: map(
          state.refreshedChartData[state.selectedChartTab].PreviewData,
          (item) => ({
            id: item.id,
            scenario: item.scenario,
            segment: item.segment,
            name: item.name,
            values: slice(
              (
                find(
                  state.refreshedChartData[state.selectedChartTab].PreviewData,
                  { id: item.id }
                ) as IChartDataSeries
              ).values,
              startIndex,
              endIndex + 1
            ),
            visible: item.visible,
          })
        ),
      };
      const newChartDataDates = slice(
        state.refreshedChartData[state.selectedChartTab].Dates,
        startIndex,
        endIndex + 1
      );
      setState({
        selectedChartRange: {
          ...state.selectedChartRange,
          [state.selectedChartTab]: [startRange, endRange],
        },
        chartData: {
          ...state.chartData,
          [state.selectedChartTab]: {
            Dates: newChartDataDates,
            PublishedData: structuredClone(newChartDataSeries.PublishedData),
            PreviewData: structuredClone(newChartDataSeries.PreviewData),
          },
        },
        selectedChartRangeIndex: {
          ...state.selectedChartRangeIndex,
          [state.selectedChartTab]: [startIndex, endIndex],
        },
      });
    }
  };

  const handleChangeChartType = (chartType: string): void => {
    setState({
      selectedChartType: {
        ...state.selectedChartType,
        [state.selectedChartTab]: chartType,
      },
    });
  };

  const handleChangeChartDataType = (chartDataType: string): void => {
    setState({
      selectedChartDataType: {
        ...state.selectedChartDataType,
        [state.selectedChartTab]: chartDataType,
      },
    });
  };

  const handleLegendChange = (id: number, visible: boolean) => {
    setState({
      chartData: {
        ...state.chartData,
        [state.selectedChartTab]: {
          Dates: state.chartData[state.selectedChartTab].Dates,
          PublishedData: map(
            state.chartData[state.selectedChartTab].PublishedData,
            (item) => (item.id === id ? { ...item, visible } : item)
          ),
          PreviewData: map(
            state.chartData[state.selectedChartTab].PreviewData,
            (item) => (item.id === id ? { ...item, visible } : item)
          ),
        },
      },
      refreshedChartData: {
        ...state.refreshedChartData,
        [state.selectedChartTab]: {
          Dates: state.refreshedChartData[state.selectedChartTab].Dates,
          PublishedData: map(
            state.refreshedChartData[state.selectedChartTab].PublishedData,
            (item) => (item.id === id ? { ...item, visible } : item)
          ),
          PreviewData: map(
            state.refreshedChartData[state.selectedChartTab].PreviewData,
            (item) => (item.id === id ? { ...item, visible } : item)
          ),
        },
      },
    });
  };

  const formatKey = selectedRowIdentifier?.FormatKey ?? 0;
  const formatSpec = find(formatParameters, { formatKey }) as
    IFormatOutputParameters | undefined;
  const selectedPrefixValue = formatSpec?.numberType === '$' ? '$' : '';
  const selectedSuffixValue = formatSpec?.numberType === 'Percent' ? '%' : '';
  const magnitudeTruncation = (formatSpec?.magnitudeTruncation as number) ?? 0;

  const stableChartData = useMemo(
    () => ({
      PreviewData:
        state.chartData[state.selectedChartTab]?.PreviewData ||
        transformChartData((chartData as any)?.PreviewData),
      PublishedData:
        state.chartData[state.selectedChartTab]?.PublishedData ||
        transformChartData((chartData as any)?.PublishedData),
      Dates:
        state.chartData[state.selectedChartTab]?.Dates ||
        (chartData as any)?.Dates ||
        [],
    }),
    [state.chartData, state.selectedChartTab, chartData]
  );

  const stableRefreshedChartData = useMemo(
    () => ({
      PreviewData:
        state.refreshedChartData[state.selectedChartTab]?.PreviewData ||
        transformChartData((chartData as any)?.PreviewData),
      PublishedData:
        state.refreshedChartData[state.selectedChartTab]?.PublishedData ||
        transformChartData((chartData as any)?.PublishedData),
      Dates:
        state.refreshedChartData[state.selectedChartTab]?.Dates ||
        (chartData as any)?.Dates ||
        [],
    }),
    [state.refreshedChartData, state.selectedChartTab, chartData]
  );

  return (
    <GenerateChartPanel
      tableExportRef={tableExportRef}
      disableGutters={disableGutters}
      frameChart={frameChart}
      chartData={stableChartData}
      refreshedChartData={stableRefreshedChartData}
      timescale={(timescaleString as Timescale) ?? Timescale.Yearly}
      formatKey={formatKey}
      yAxisTitle={
        selectedRowIdentifier?.Name ??
        (chartData as any)?.PublishedData?.[0]?.output ??
        ''
      }
      chartTitle={selectedChartTab?.title as string}
      prefix={selectedPrefixValue}
      suffix={selectedSuffixValue}
      magnitudeTruncation={magnitudeTruncation}
      decimalPlaces={2}
      chartDateType={
        includes(
          [Timescale.Weekly, Timescale.Monthly],
          timescaleString as Timescale
        )
          ? ((dateFormats(timescaleString as Timescale) as string[])[0] ?? '')
          : ''
      }
      selectedChartRange={state.selectedChartRange[state.selectedChartTab]}
      selectedChartType={state.selectedChartType[state.selectedChartTab]}
      selectedChartDataType={
        state.selectedChartDataType[state.selectedChartTab]
      }
      handleChangeChartRange={handleChangeChartRange}
      handleChangeChartType={handleChangeChartType}
      handleChangeChartDataType={handleChangeChartDataType}
      handleLegendChange={handleLegendChange}
      isPanelCollapsed={false}
      showScenarios={false}
    />
  );
};
